import { useState } from "react";
import type { AiSnapshotDiffResponse } from "../services/api";
import { analyzeRequisitionSnapshot, updateProject } from "../services/api";
import type { AlertState } from "../utils/errors";
import { getErrorAlertMessage } from "../utils/errors";

export function RRSnapshotDiff() {
  const [snapshotInput, setSnapshotInput] = useState("");
  const [snapshotResult, setSnapshotResult] = useState<AiSnapshotDiffResponse | null>(null);
  const [expireSelection, setExpireSelection] = useState<Record<string, boolean>>({});
  const [alert, setAlert] = useState<AlertState | null>(null);
  const [isAnalyzingSnapshot, setIsAnalyzingSnapshot] = useState(false);
  const [isExpiring, setIsExpiring] = useState(false);

  async function handleSnapshotAnalyze() {
    if (!snapshotInput.trim()) {
      setAlert({ type: "error", message: "Paste the exported RR table before running the snapshot analysis." });
      return;
    }
    setIsAnalyzingSnapshot(true);
    setAlert(null);
    try {
      const result = await analyzeRequisitionSnapshot(snapshotInput);
      setSnapshotResult(result);
      setExpireSelection({});
      const message =
        result.missing_projects.length === 0
          ? `Snapshot processed. Parsed ${result.parsed_count} RRs; none are missing from DynamoDB.`
          : `Snapshot processed. Parsed ${result.parsed_count} RRs; ${result.missing_projects.length} existing requisitions were not in the paste.`;
      setAlert({ type: "info", message });
    } catch (err) {
      setAlert({ type: "error", message: getErrorAlertMessage("analyze the RR snapshot", err) });
    } finally {
      setIsAnalyzingSnapshot(false);
    }
  }

  async function handleExpireSelected() {
    if (!snapshotResult) {
      return;
    }
    const selectedIds = snapshotResult.missing_projects
      .map((project) => project.project_id)
      .filter((projectId) => expireSelection[projectId]);

    if (selectedIds.length === 0) {
      setAlert({ type: "error", message: "Select at least one RR to expire." });
      return;
    }

    setIsExpiring(true);
    const failures: string[] = [];
    for (const projectId of selectedIds) {
      try {
        await updateProject(projectId, { status: "EXPIRED" });
      } catch (err) {
        failures.push(`${projectId}: ${getErrorAlertMessage("expire this RR", err)}`);
      }
    }
    setIsExpiring(false);
    if (failures.length > 0) {
      setAlert({ type: "error", message: "Some RRs could not be expired.", details: failures });
    } else {
      setAlert({ type: "info", message: `Expired ${selectedIds.length} RR${selectedIds.length === 1 ? "" : "s"}.` });
    }
    setExpireSelection({});
    setSnapshotResult((previous) =>
      previous
        ? {
            ...previous,
            missing_projects: previous.missing_projects.filter((project) => !selectedIds.includes(project.project_id)),
          }
        : previous,
    );
  }

  const selectedMissingCount =
    snapshotResult?.missing_projects.filter((project) => expireSelection[project.project_id]).length ?? 0;

  return (
    <div className="layout single-column">
      {alert && (
        <div className={`alert ${alert.type}`}>
          <p>{alert.message}</p>
          {alert.details && alert.details.length > 0 && (
            <ul>
              {alert.details.map((detail) => (
                <li key={detail}>{detail}</li>
              ))}
            </ul>
          )}
        </div>
      )}
      <div className="panel">
        <h2>Snapshot Diff (AI)</h2>
        <p className="helper-text">
          Paste a tabular export (CSV, TSV, or spreadsheet copy) of all open RR&apos;s. We&apos;ll detect the RR IDs in that paste, compare them to
          what&apos;s currently stored in DynamoDB, and highlight any requisitions that have fallen out of the feed so you can expire them quickly.
        </p>
        <textarea
          rows={6}
          value={snapshotInput}
          onChange={(event) => setSnapshotInput(event.target.value)}
          placeholder="RR, Title, Status, Delivery Unit..."
        />
        <button type="button" className="primary" onClick={handleSnapshotAnalyze} disabled={isAnalyzingSnapshot}>
          {isAnalyzingSnapshot ? "Analyzing…" : "Analyze Snapshot"}
        </button>
        {snapshotResult && (
          <div className="snapshot-results" style={{ marginTop: "1rem" }}>
            <div className="helper-text">
              Parsed {snapshotResult.parsed_count} RRs from the paste. DynamoDB currently tracks {snapshotResult.existing_count} RR records.
            </div>
            {snapshotResult.records.length > 0 && (
              <div className="chip-row" style={{ marginTop: "0.5rem" }}>
                {snapshotResult.records.map((record, index) => (
                  <span key={`${record.project_id}-${index}`} className="chip">
                    {record.project_id}
                    {record.title ? ` · ${record.title}` : ""}
                  </span>
                ))}
              </div>
            )}
            <div className="detail-block" style={{ marginTop: "1rem" }}>
              <h4>Expiry Candidates (missing from snapshot) ({snapshotResult.missing_projects.length})</h4>
              {snapshotResult.missing_projects.length === 0 ? (
                <p className="muted">All stored RR&apos;s appeared in the provided snapshot.</p>
              ) : (
                <>
                  <div className="table-wrapper" style={{ marginBottom: "0.5rem" }}>
                    <table>
                      <thead>
                        <tr>
                          <th>Select</th>
                          <th>RR</th>
                          <th>Status</th>
                          <th>Title</th>
                        </tr>
                      </thead>
                      <tbody>
                        {snapshotResult.missing_projects.map((project) => (
                          <tr key={project.project_id}>
                            <td>
                              <input
                                type="checkbox"
                                checked={Boolean(expireSelection[project.project_id])}
                                onChange={(event) =>
                                  setExpireSelection((prev) => ({
                                    ...prev,
                                    [project.project_id]: event.target.checked,
                                  }))
                                }
                              />
                            </td>
                            <td>{project.project_id}</td>
                            <td>{project.status ?? "-"}</td>
                            <td>{project.title ?? "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <button
                    type="button"
                    className="danger"
                    onClick={() => void handleExpireSelected()}
                    disabled={isExpiring || selectedMissingCount === 0}
                  >
                    {isExpiring
                      ? "Expiring…"
                      : selectedMissingCount > 0
                        ? `Expire ${selectedMissingCount} Selected`
                        : "Expire Selected"}
                  </button>
                </>
              )}
            </div>
            <div className="detail-block" style={{ marginTop: "1rem" }}>
              <h4>New Requisitions in Snapshot ({snapshotResult.new_projects?.length ?? 0})</h4>
              {snapshotResult.new_projects && snapshotResult.new_projects.length > 0 ? (
                <div className="table-wrapper" style={{ marginBottom: "0.5rem" }}>
                  <table>
                    <thead>
                      <tr>
                        <th>RR</th>
                        <th>Status</th>
                        <th>Title</th>
                        <th>Delivery Unit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {snapshotResult.new_projects.map((project, index) => (
                        <tr key={`${project.project_id}-${index}`}>
                          <td>{project.project_id}</td>
                          <td>{project.status ?? "-"}</td>
                          <td>{project.title ?? "-"}</td>
                          <td>{project.delivery_unit ?? "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="muted">No new RR&apos;s detected in the snapshot.</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
